import { AstroTime } from 'astronomy-engine';

/**
 * Time scales (decision D2):
 * - the app state stores **UTC milliseconds** since the Unix epoch;
 * - astronomy-engine works with `AstroTime` (UT and TT days since J2000);
 * - trajectories use **days since J2000 TDB** (TDB ≈ TT within 2 ms, which we ignore).
 */

/** Milliseconds per day. */
export const DAY_MS = 86_400_000;

/** Julian date of the J2000 epoch (2000-01-01 12:00 TT). */
export const J2000_JD = 2_451_545.0;

/** Unix time (ms) of 2000-01-01 12:00 UTC, the zero point of astronomy-engine's UT day count. */
export const J2000_UTC_MS = Date.UTC(2000, 0, 1, 12, 0, 0);

/**
 * Leap seconds: [UTC ms at which TAI − UTC changes, new TAI − UTC in seconds]. Since 1972 UTC is TAI minus an integer
 * number of seconds, so TT = UTC + (TAI − UTC) + 32.184 s exactly. Update when IERS announces a new leap second.
 */
const LEAP_SECONDS: readonly (readonly [number, number])[] = [
  [Date.UTC(1972, 0, 1), 10],
  [Date.UTC(1972, 6, 1), 11],
  [Date.UTC(1973, 0, 1), 12],
  [Date.UTC(1974, 0, 1), 13],
  [Date.UTC(1975, 0, 1), 14],
  [Date.UTC(1976, 0, 1), 15],
  [Date.UTC(1977, 0, 1), 16],
  [Date.UTC(1978, 0, 1), 17],
  [Date.UTC(1979, 0, 1), 18],
  [Date.UTC(1980, 0, 1), 19],
  [Date.UTC(1981, 6, 1), 20],
  [Date.UTC(1982, 6, 1), 21],
  [Date.UTC(1983, 6, 1), 22],
  [Date.UTC(1985, 6, 1), 23],
  [Date.UTC(1988, 0, 1), 24],
  [Date.UTC(1990, 0, 1), 25],
  [Date.UTC(1991, 0, 1), 26],
  [Date.UTC(1992, 6, 1), 27],
  [Date.UTC(1993, 6, 1), 28],
  [Date.UTC(1994, 6, 1), 29],
  [Date.UTC(1996, 0, 1), 30],
  [Date.UTC(1997, 6, 1), 31],
  [Date.UTC(1999, 0, 1), 32],
  [Date.UTC(2006, 0, 1), 33],
  [Date.UTC(2009, 0, 1), 34],
  [Date.UTC(2012, 6, 1), 35],
  [Date.UTC(2015, 6, 1), 36],
  [Date.UTC(2017, 0, 1), 37],
];

const TT_MINUS_TAI_S = 32.184;

/**
 * TT − UTC in seconds. Exact from 1972 on (leap-second table); before 1972 astronomy-engine's ΔT model (TT − UT1)
 * is used, which is within a fraction of a second of UTC there.
 */
export function ttMinusUtcSeconds(ms: number): number {
  const first = LEAP_SECONDS[0]!;
  if (ms < first[0]) {
    const ut = (ms - J2000_UTC_MS) / DAY_MS;
    return (new AstroTime(ut).tt - ut) * 86400;
  }
  let taiMinusUtc = first[1];
  for (const [start, value] of LEAP_SECONDS) {
    if (ms < start) break;
    taiMinusUtc = value;
  }
  return taiMinusUtc + TT_MINUS_TAI_S;
}

/** UTC milliseconds → days since J2000 TDB. */
export function utcMsToTdbDays(ms: number): number {
  return (ms - J2000_UTC_MS + ttMinusUtcSeconds(ms) * 1000) / DAY_MS;
}

/** UTC milliseconds → astronomy-engine time (constructed from TT, so ephemerides use the exact time scale). */
export function utcMsToAstroTime(ms: number): AstroTime {
  return AstroTime.FromTerrestrialTime(utcMsToTdbDays(ms));
}

/** Days since J2000 TDB → UTC milliseconds. */
export function tdbDaysToUtcMs(tdb: number): number {
  const ttMs = J2000_UTC_MS + tdb * DAY_MS;
  let ms = ttMs - 69_184;
  for (let i = 0; i < 3; i++) ms = ttMs - ttMinusUtcSeconds(ms) * 1000;
  return ms;
}

/** Days since J2000 TDB → astronomy-engine time. */
export function tdbDaysToAstroTime(tdb: number): AstroTime {
  return AstroTime.FromTerrestrialTime(tdb);
}

/** Julian date (TDB) → days since J2000 TDB. */
export function jdToTdbDays(jd: number): number {
  return jd - J2000_JD;
}

/** Parses an ISO date or date-time (always interpreted as UTC) into UTC milliseconds. */
export function parseUtc(iso: string): number {
  const s = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso}T00:00:00Z` : /[zZ]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`;
  const ms = Date.parse(s);
  if (Number.isNaN(ms)) throw new Error(`Invalid date: ${iso}`);
  return ms;
}

export type DatePrecision = 'day' | 'minute';

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/**
 * Formats a UTC timestamp (decision D1: UTC everywhere).
 * en: `1979-07-09 22:29 UTC`, de: `09.07.1979 22:29 UTC`.
 */
export function formatUtc(ms: number, lang: string, precision: DatePrecision = 'minute'): string {
  const d = new Date(ms);
  const y = d.getUTCFullYear();
  const mo = pad(d.getUTCMonth() + 1);
  const day = pad(d.getUTCDate());
  const date = lang === 'de' ? `${day}.${mo}.${y}` : `${y}-${mo}-${day}`;
  if (precision === 'day') return date;
  return `${date} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`;
}

/** Adds calendar months in UTC (day clamped by the Date rules, e.g. 31 Jan + 1 month = 3 Mar). */
export function addUtcMonths(ms: number, months: number): number {
  const d = new Date(ms);
  d.setUTCMonth(d.getUTCMonth() + months);
  return d.getTime();
}
